import { FormattedMenuSectionInfo } from 'api'
import EDIT_ICON from 'assets/icons/edit-clear-icon.svg'
import { FC, Fragment, useEffect, useRef } from 'react'
import { useIsMobile } from 'hooks'
import theme from 'theme'

import { ModifierValuesMap } from './MenuSectionEditor'
import * as Styled from './styles'
import { ExpandNestedModifiersMap } from './types'
import SortableModifierContainer from './components/SortableModifierContainer'
import SortableModifierItem from './components/SortableModifierItem'
import SortableModifierGroupContainer from './components/SortableModifierGroupContainer'
import SortableModifierGroupItem from './components/SortableModifierGroupItem'

type Props = {
  customization: FormattedMenuSectionInfo
  groupModifiers?: FormattedMenuSectionInfo[]
  expandAllModifiers?: boolean
  modifierValues: ModifierValuesMap
  onChangeModifierValues?: ({
    modId,
    price,
    title,
  }: {
    modId: string
    price?: number
    title?: string
  }) => void
  onDeleteModifier: (modId: string, customization: FormattedMenuSectionInfo) => void
  onModifiersReorder?: (customizationId: string, newModifiers: FormattedMenuSectionInfo[]) => void
  onNestedModifiersReorder?: (parentModifierId: string, nestedCustomizationId: string, newNestedModifiers: FormattedMenuSectionInfo[]) => void
  onNestedCustomizationGroupsReorder?: (parentModifierId: string, newNestedCustomizationGroups: FormattedMenuSectionInfo[]) => void
  isItemModal?: boolean
  setEditGroup?: (groupId: string) => void
  isEditGroup?: boolean
  groupValues?: { title?: string; min_permitted?: number; max_permitted?: number }
  updateGroupValues?: ({
    title,
    min_permitted,
    max_permitted,
  }: {
    title?: string
    min_permitted?: number
    max_permitted?: number
  }) => void
  isChildItem?: boolean
  isPrinterClient?: boolean
  dragHandle?: React.ReactNode
  isDragging?: boolean
  expandNestedModifiers?: ExpandNestedModifiersMap
  setExpandNestedModifiers?: (updater: ExpandNestedModifiersMap | ((prev: ExpandNestedModifiersMap) => ExpandNestedModifiersMap)) => void
}

export type ModifiersById = {
  [id: string]: { title: string; price: number }
}

const ModifierSubsectionEditor: FC<Props> = ({
  customization,
  groupModifiers,
  // expandAllModifiers,
  modifierValues,
  onChangeModifierValues,
  onDeleteModifier,
  onModifiersReorder,
  onNestedModifiersReorder,
  onNestedCustomizationGroupsReorder,
  isItemModal,
  setEditGroup,
  isEditGroup,
  isChildItem,
  isPrinterClient,
  dragHandle,
  isDragging,
  expandNestedModifiers,
  setExpandNestedModifiers,
}) => {
  const { isMobile } = useIsMobile()
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (inputRef.current && isEditGroup) {
      inputRef.current?.focus()
    }
  }, [isEditGroup])

  const handleModifiersReorder = (newModifiers: FormattedMenuSectionInfo[]) => {
    // Update local state immediately for responsive UI
    if (onModifiersReorder) {
      onModifiersReorder(customization.id, newModifiers)
    }
  }

  const handleNestedModifiersReorder = (nestedCustomization: FormattedMenuSectionInfo, newNestedModifiers: FormattedMenuSectionInfo[], parentModifierId: string) => {
    // Only propagate to parent for API calls - don't call onModifiersReorder
    // Local state updates will be handled by the parent component
    if (onNestedModifiersReorder) {
      onNestedModifiersReorder(parentModifierId, nestedCustomization.id, newNestedModifiers)
    }
  }

  const handleNestedCustomizationGroupsReorder = (parentModifierId: string, newNestedCustomizationGroups: FormattedMenuSectionInfo[]) => {
    // Only propagate to parent for API calls
    if (onNestedCustomizationGroupsReorder) {
      onNestedCustomizationGroupsReorder(parentModifierId, newNestedCustomizationGroups)
    }
  }

  // hide the child items when dragging
  if (isDragging) {
    return (
      <Styled.SubsectionContainer style={{ paddingLeft: isChildItem ? (isMobile ? '60px' : '90px') : 0 }}>
        <Styled.HeaderRow style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {dragHandle}
            <Styled.ModifierTitle marginLeft={'12px'}>
              {customization.attributes.title}
            </Styled.ModifierTitle>
          </div>
        </Styled.HeaderRow>
      </Styled.SubsectionContainer>
    )
  }

  return (
    <Styled.SubsectionContainer style={{ paddingLeft: isChildItem ? (isMobile ? '60px' : '90px') : 0 }}>
      <Styled.HeaderRow style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {dragHandle}
          {isItemModal && setEditGroup && (
            <Styled.IconButton
              onClick={() => {
                setEditGroup(customization.id)
              }}
              margin='10px'
            >
              <img src={EDIT_ICON} alt='edit' />
            </Styled.IconButton>
          )}

          <Styled.ModifierTitle marginLeft={'12px'}>
            {customization.attributes.title}
          </Styled.ModifierTitle>
        </div>

        <Styled.TopRowText textAlign='end' fontWeight={theme.fontWeight.normal}>
          Min: {customization.attributes.min_permitted} / Max:{' '}
          {customization.attributes.max_permitted}
        </Styled.TopRowText>
      </Styled.HeaderRow>

      {groupModifiers && (
        <SortableModifierContainer
          modifiers={groupModifiers}
          onModifiersReorder={handleModifiersReorder}
          isPrinterClient={isPrinterClient}
        >
          {groupModifiers.map((modifier) => {
            const nestedModGroups = modifier?.children.customizations

            return (
              <Fragment key={modifier.id}>
                <SortableModifierItem
                  modifier={modifier}
                  customization={customization}
                  modifierValues={modifierValues[modifier.id]}
                  onChangeModifierValues={onChangeModifierValues}
                  onDeleteModifier={onDeleteModifier}
                  isPrinterClient={isPrinterClient}
                  isItemModal={isItemModal}
                  isEditor={true}
                  modifiersExist={!!modifier.children.customizations?.length}
                  onRowClick={() =>
                    setExpandNestedModifiers?.((prevExpandStatus) => ({
                      ...prevExpandStatus,
                      [modifier.id]: !prevExpandStatus[modifier.id],
                    }))
                  }
                  isExpanded={expandNestedModifiers?.[modifier.id]}
                  isSelected={!modifier.attributes.suspend_until || new Date(modifier.attributes.suspend_until) < new Date()}
                />

                {/* Another level of nesting: customization group & modifiers */}
                {expandNestedModifiers?.[modifier.id] && nestedModGroups && (
                  <SortableModifierGroupContainer
                    customizationGroups={nestedModGroups}
                    onCustomizationGroupsReorder={(newNestedCustomizationGroups) => handleNestedCustomizationGroupsReorder(modifier.id, newNestedCustomizationGroups)}
                    isPrinterClient={isPrinterClient}
                  >
                    {nestedModGroups.map((customization) => (
                      <SortableModifierGroupItem
                        key={customization.id}
                        customizationGroup={customization}
                        isPrinterClient={isPrinterClient}
                        dragHandleStyle={{paddingLeft: '100px'}}
                      >
                        {(dragHandle, isAnyGroupDragging) => (
                          <Styled.SubsectionContainer>
                            <Styled.HeaderRow>
                              {dragHandle}
                              <Styled.ModifierTitle marginLeft={isMobile ? '30px' : '16px'}>
                                {customization.attributes.title}
                              </Styled.ModifierTitle>
                              <Styled.TopRowText textAlign='end' fontWeight={theme.fontWeight.normal}>
                                Min: {customization.attributes.min_permitted} / Max:{' '}
                                {customization.attributes.max_permitted}
                              </Styled.TopRowText>
                            </Styled.HeaderRow>
                            {customization.children.customization_options && !isAnyGroupDragging && (
                              <SortableModifierContainer
                                modifiers={customization.children.customization_options}
                                onModifiersReorder={(newNestedModifiers) => handleNestedModifiersReorder(customization, newNestedModifiers, modifier.id)}
                                isPrinterClient={isPrinterClient}
                              >
                                {customization.children.customization_options.map((nestedModifier) => (
                                  <SortableModifierItem
                                    key={nestedModifier.id}
                                    modifier={nestedModifier}
                                    customization={customization}
                                    modifierValues={modifierValues[nestedModifier.id]}
                                    onChangeModifierValues={onChangeModifierValues}
                                    onDeleteModifier={onDeleteModifier}
                                    isPrinterClient={isPrinterClient}
                                    isItemModal={isItemModal}
                                    isEditor={true}
                                    isSelected={!nestedModifier.attributes.suspend_until || new Date(nestedModifier.attributes.suspend_until) < new Date()}
                                    isNested={true}
                                  />
                                ))}
                              </SortableModifierContainer>
                            )}
                          </Styled.SubsectionContainer>
                        )}
                      </SortableModifierGroupItem>
                    ))}
                  </SortableModifierGroupContainer>
                )}
              </Fragment>
            )
          })}
        </SortableModifierContainer>
      )}
    </Styled.SubsectionContainer>
  )
}

ModifierSubsectionEditor.displayName = 'ModifierSubsectionEditor'

export default ModifierSubsectionEditor
